import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Mail, ArrowLeft, CheckCircle2, Shield, KeyRound } from "lucide-react";
import { motion } from "framer-motion";
import { useRecoveryPassword } from "@/hooks/useAuth";
import { useForm } from "react-hook-form";
import {
  recoveryPasswordSchema,
  type RecoveryPasswordFormData,
} from "@/schemas/authSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import PrefLogo from "@/assets/logo_municipal.svg";

export const RecoveryPassword = () => {
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const recoveryPassword = useRecoveryPassword();

  const {
    register: registerField,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RecoveryPasswordFormData>({
    resolver: zodResolver(recoveryPasswordSchema),
  });

  const email = watch("email");

  const submitRecoveryPassword = async (data: RecoveryPasswordFormData) => {
    await recoveryPassword.mutateAsync(data, {
      onSuccess: () => {
        setEmailSent(true);
      },
    });
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-[hsl(var(--primary))]/20 via-[hsl(var(--primary))]/5 to-[hsl(var(--secondary))]/10">
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -top-24 -left-24 w-96 h-96 bg-[hsl(var(--primary))]/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, -90, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -bottom-24 -right-24 w-96 h-96 bg-[hsl(var(--secondary))]/10 rounded-full blur-3xl"
        />
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:20px_20px]" />
        </div>
      </div>

      {/* Container principal */}
      <div className="w-full max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Coluna esquerda - Informações */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden md:block"
        >
          <div className="space-y-8">
            <div>
              <div className="mb-2">
                <p className="text-sm font-semibold text-[hsl(var(--primary))] uppercase tracking-wider mb-2">
                  
                </p>
                <h1 className="text-6xl font-black mb-2 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] bg-clip-text text-transparent">
                  Vida+Simples
                </h1>
                <p className="text-lg font-medium text-gray-700">Ceará</p>
              </div>
              <p className="text-xl text-gray-600 leading-relaxed mt-6">
                Recupere o acesso à sua conta de forma rápida e segura
              </p>
            </div>

            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <KeyRound className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Recuperação Segura
                  </h3>
                  <p className="text-sm text-gray-600">
                    Processo simples e seguro para redefinir sua senha
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <Shield className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Proteção de Dados
                  </h3>
                  <p className="text-sm text-gray-600">
                    Suas informações são protegidas com criptografia
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Coluna direita - Formulário */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full"
        >
          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="space-y-4 pb-8">
              <div className="flex items-center justify-center mb-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                >
                  <img
                    src={PrefLogo}
                    alt="Logo Municipal"
                    className="h-25 w-25"
                  />
                </motion.div>
              </div>
              {!emailSent ? (
                <>
                  <CardTitle className="text-3xl font-bold text-center text-gray-900">
                    Esqueceu sua senha?
                  </CardTitle>
                  <p className="text-center text-muted-foreground text-sm">
                    Digite seu e-mail para receber instruções de recuperação
                  </p>
                </>
              ) : (
                <>
                  <div className="flex justify-center mb-2">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-10 w-10 text-green-600" />
                    </div>
                  </div>
                  <CardTitle className="text-3xl font-bold text-center text-gray-900">
                    E-mail enviado!
                  </CardTitle>
                  <p className="text-center text-muted-foreground text-sm">
                    Verifique sua caixa de entrada e siga as instruções
                  </p>
                </>
              )}
            </CardHeader>
            <CardContent>
              {!emailSent ? (
                <form
                  onSubmit={handleSubmit(submitRecoveryPassword)}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">
                      Email
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        className={`pl-10 pr-4 focus-visible:ring-[hsl(var(--primary))] ${errors.email ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                        {...registerField("email")}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-sm text-red-500">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={recoveryPassword.isPending}
                    className="w-full text-white cursor-pointer h-12 text-base font-medium bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] hover:from-[hsl(var(--primary))]/90 hover:to-[hsl(var(--secondary))]/90 shadow-lg"
                  >
                    {recoveryPassword.isPending ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Enviando...
                      </div>
                    ) : (
                      "Enviar instruções"
                    )}
                  </Button>

                  <Link
                    to="/login"
                    className="flex items-center justify-center gap-2 text-sm text-[hsl(var(--primary))] hover:underline font-medium"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Voltar para login
                  </Link>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-sm text-gray-700 text-center">
                      Enviamos um e-mail para:{" "}
                      <span className="font-semibold text-[hsl(var(--primary))]">
                        {email}
                      </span>
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate("/login")}
                    className="w-full text-white cursor-pointer h-12 text-base font-medium bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] hover:from-[hsl(var(--primary))]/90 hover:to-[hsl(var(--secondary))]/90 shadow-lg"
                  >
                    Voltar para login
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Footer mobile */}
          <p className="text-center text-sm text-gray-600 mt-8 md:hidden">
            © 2026 Vida Mais Simples - Frecheirinha, CE
          </p>
        </motion.div>
      </div>

      {/* Footer desktop */}
      <div className="absolute bottom-6 left-0 right-0 text-center hidden md:block">
        <p className="text-sm text-gray-600">
          © 2026 Vida Mais Simples - Frecheirinha, CE
        </p>
      </div>
    </div>
  );
};
